import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { applyInviteToApplicantCaches } from './applicantInviteCache';
import screeningClient from './apiClient';
import {
  ApplicantFilter,
  DecisionInput,
  JobFormInput,
  JobSettingsInput,
  SpecItem,
  ApplicationQuestion,
} from './types';

/** Query keys exactly as specified in the build spec. */
export const queryKeys = {
  me: ['me'] as const,
  onboardingQuestions: ['onboarding', 'questions'] as const,
  jobs: ['jobs'] as const,
  job: (id: string) => ['job', id] as const,
  applicants: (id: string, filter: ApplicantFilter) =>
    ['job', id, 'applicants', filter] as const,
  applicant: (jobId: string, candidateId: string) =>
    ['applicant', jobId, candidateId] as const,
  applicantInterview: (jobId: string, candidateId: string) =>
    ['applicant', jobId, candidateId, 'interview'] as const,
  board: (token: string) => ['board', token] as const,
  teams: ['teams'] as const,
  team: (id: string) => ['team', id] as const,
  map: (band?: string, teamId?: string, jobId?: string) =>
    ['map', band, teamId, jobId] as const,
  mapSummary: ['map', 'summary'] as const,
  mapOverview: ['map', 'overview'] as const,
  mapTeam: (id: string) => ['map', 'team', id] as const,
  mapRole: (id: string) => ['map', 'role', id] as const,
  mapPerson: (id: string, roleId?: string) =>
    ['map', 'person', id, roleId || ''] as const,
  mapSearch: (q: string) => ['map', 'search', q] as const,
  mapShortlist: ['map', 'shortlist'] as const,
  mapSources: ['map', 'sources'] as const,
  mapV2: ['map', 'v2'] as const,
  mapHomeV2: ['map', 'v2', 'home'] as const,
  mapSearchV2: (q: string, drop: string, sort: string) =>
    ['map', 'v2', 'search', q, drop, sort] as const,
  mapRoleV2: (id: string) => ['map', 'v2', 'role', id] as const,
  mapPersonV2: (id: string, roleId?: string) =>
    ['map', 'v2', 'person', id, roleId || ''] as const,
};

/* ------------------------------------------------------------------ */
/* Queries                                                            */
/* ------------------------------------------------------------------ */

export const useMe = () =>
  useQuery({ queryKey: queryKeys.me, queryFn: screeningClient.me });

export const useOnboardingQuestions = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.onboardingQuestions,
    queryFn: screeningClient.onboardingQuestions,
    enabled,
  });

export const useJobs = () =>
  useQuery({ queryKey: queryKeys.jobs, queryFn: screeningClient.listJobs });

export const useTeams = () =>
  useQuery({ queryKey: queryKeys.teams, queryFn: screeningClient.listTeams });

export const useMap = (band?: string, teamId?: string, jobId?: string) =>
  useQuery({
    queryKey: queryKeys.map(band, teamId, jobId),
    queryFn: () =>
      screeningClient.listMap({ band, team_id: teamId, job_id: jobId }),
  });

export const useMapSummary = () =>
  useQuery({
    queryKey: queryKeys.mapSummary,
    queryFn: screeningClient.mapSummary,
  });

export const useMapOverview = (enabled = true) =>
  useQuery({
    queryKey: queryKeys.mapOverview,
    queryFn: screeningClient.mapOverview,
    enabled,
  });

export const useMapTeam = (teamId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.mapTeam(teamId ?? ''),
    queryFn: () => screeningClient.mapTeam(teamId as string),
    enabled: Boolean(teamId),
  });

export const useMapRole = (roleId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.mapRole(roleId ?? ''),
    queryFn: () => screeningClient.mapRole(roleId as string),
    enabled: Boolean(roleId),
  });

export const useMapPersonView = (
  personId: string | undefined,
  roleId?: string
) =>
  useQuery({
    queryKey: queryKeys.mapPerson(personId ?? '', roleId),
    queryFn: () => screeningClient.mapPersonView(personId as string, roleId),
    enabled: Boolean(personId),
  });

export const useMapSearch = (q: string, outside = false) =>
  useQuery({
    queryKey: [...queryKeys.mapSearch(q), outside ? 'outside' : 'pool'],
    queryFn: () => screeningClient.mapSearch(q, outside),
    enabled: q.trim().length > 1,
  });

export const useMapShortlist = () =>
  useQuery({
    queryKey: queryKeys.mapShortlist,
    queryFn: screeningClient.mapShortlist,
  });

export const useMapSources = () =>
  useQuery({
    queryKey: queryKeys.mapSources,
    queryFn: screeningClient.mapSources,
  });

export const useMapHomeV2 = () =>
  useQuery({
    queryKey: queryKeys.mapHomeV2,
    queryFn: screeningClient.mapHomeV2,
  });

export const usePrefetchNav = () => {
  const client = useQueryClient();
  return (key: string) => {
    if (key === 'map' || key === 'people') {
      client.prefetchQuery({
        queryKey: queryKeys.mapHomeV2,
        queryFn: screeningClient.mapHomeV2,
      });
    } else if (key === 'jobs') {
      client.prefetchQuery({
        queryKey: queryKeys.jobs,
        queryFn: screeningClient.listJobs,
      });
    } else if (key === 'groups') {
      client.prefetchQuery({
        queryKey: queryKeys.teams,
        queryFn: screeningClient.listTeams,
      });
      client.prefetchQuery({
        queryKey: queryKeys.mapSummary,
        queryFn: screeningClient.mapSummary,
      });
    } else if (key === 'settings') {
      client.prefetchQuery({
        queryKey: queryKeys.mapSources,
        queryFn: screeningClient.mapSources,
      });
    }
  };
};

export const useMapSearchV2 = (input: {
  q: string;
  drop: string;
  sort: string;
}) =>
  useQuery({
    queryKey: queryKeys.mapSearchV2(input.q, input.drop, input.sort),
    queryFn: () =>
      screeningClient.mapSearchV2({
        q: input.q,
        drop: input.drop || undefined,
        sort: input.sort || undefined,
      }),
    enabled: input.q.trim().length > 1,
    keepPreviousData: true,
  });

export const useMapRoleV2 = (roleId: string | undefined) =>
  useQuery({
    queryKey: queryKeys.mapRoleV2(roleId ?? ''),
    queryFn: () => screeningClient.mapRoleV2(roleId as string),
    enabled: Boolean(roleId),
  });

export const useMapPersonV2 = (personId: string | undefined, roleId?: string) =>
  useQuery({
    queryKey: queryKeys.mapPersonV2(personId ?? '', roleId),
    queryFn: () => screeningClient.mapPersonV2(personId as string, roleId),
    enabled: Boolean(personId),
  });

const useMapV2Mutation = <TInput>(
  run: (input: TInput) => Promise<{ sentence: string }>
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.mapV2 }),
  });
};

export const useShortlistToggleV2 = () =>
  useMapV2Mutation(
    (input: { roleId: string; personId: string; on: boolean }) =>
      input.on
        ? screeningClient.mapShortlistAddV2(input.roleId, input.personId)
        : screeningClient.mapShortlistRemoveV2(input.roleId, input.personId)
  );

export const useShortlistSendV2 = () =>
  useMapV2Mutation((input: { roleId: string; note: string }) =>
    screeningClient.mapShortlistSendV2(input.roleId, input.note)
  );

export const useAskV2 = () =>
  useMapV2Mutation((input: { personId: string; sentence: string }) =>
    screeningClient.mapAskV2(input.personId, input.sentence)
  );

export const usePlaceV2 = () =>
  useMapV2Mutation((input: { personId: string; employer?: string }) =>
    screeningClient.mapPlaceV2(input.personId, input.employer)
  );

export const useMapActionV2 = () =>
  useMapV2Mutation(screeningClient.mapActionV2);

export const useJob = (id: string | undefined) =>
  useQuery({
    queryKey: queryKeys.job(id ?? ''),
    queryFn: () => screeningClient.getJob(id as string),
    enabled: Boolean(id),
  });

export const useApplicants = (
  jobId: string | undefined,
  filter: ApplicantFilter
) =>
  useQuery({
    queryKey: queryKeys.applicants(jobId ?? '', filter),
    queryFn: () => screeningClient.listApplicants(jobId as string, filter),
    enabled: Boolean(jobId),
  });

export const useApplicant = (
  jobId: string | undefined,
  candidateId: string | undefined
) =>
  useQuery({
    queryKey: queryKeys.applicant(jobId ?? '', candidateId ?? ''),
    queryFn: () =>
      screeningClient.getApplicant(jobId as string, candidateId as string),
    enabled: Boolean(jobId && candidateId),
  });

export const useApplicantInterview = (
  jobId: string | undefined,
  candidateId: string | undefined
) =>
  useQuery({
    queryKey: queryKeys.applicantInterview(jobId ?? '', candidateId ?? ''),
    queryFn: () =>
      screeningClient.getApplicantInterview(
        jobId as string,
        candidateId as string
      ),
    enabled: Boolean(jobId && candidateId),
    refetchInterval: (data) => (data && !data.analysis ? 5000 : false),
  });

/* ------------------------------------------------------------------ */
/* Mutations                                                          */
/* ------------------------------------------------------------------ */

export const useCreateJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: JobFormInput) => screeningClient.createJob(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.jobs }),
  });
};

export const useUpdateJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      payload: Partial<JobFormInput> & {
        spec_items?: SpecItem[];
        application_questions?: ApplicationQuestion[];
      }
    ) => screeningClient.updateJob(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.job(id) });
      qc.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
};

export const useRegenerateSpecs = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => screeningClient.regenerateSpecs(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.job(id) }),
  });
};

export const usePublishJob = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => screeningClient.publishJob(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.job(id) });
      qc.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
};

export const useUpdateSettings = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: JobSettingsInput) =>
      screeningClient.updateSettings(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.job(id) });
      qc.invalidateQueries({ queryKey: queryKeys.jobs });
    },
  });
};

export const useSetDecision = (jobId: string, candidateId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: DecisionInput) =>
      screeningClient.setDecision(jobId, candidateId, payload),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.applicant(jobId, candidateId),
      });
      qc.invalidateQueries({ queryKey: ['job', jobId, 'applicants'] });
    },
  });
};

export const useSendInvite = (jobId: string, candidateId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => screeningClient.sendInvite(jobId, candidateId),
    onSuccess: () => applyInviteToApplicantCaches(qc, jobId, candidateId),
  });
};
